import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, Button, Paper, Alert, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { startScan, clearScanError } from '../store/scanSlice.js';
import { api } from '../api/client.js';
import type { RootState, AppDispatch } from '../store/index.js';
import { colors } from '../styles/theme.js';
import { useAuth } from '../hooks/useAuth.js';
import { UserRole } from '../../shared/types.js';
import {
  SCORE_CARD_HTML_TEMPLATE_IDS,
  SCORE_CARD_HTML_TEMPLATE_LABELS,
  type ScoreCardHtmlTemplateId,
} from '../../shared/scoreCardHtmlTemplates.js';

interface TemplateSummary {
  id: number;
  internal_name: string;
  display_name: string;
  is_default: boolean;
  emoji: string | null;
}

function pickDefaultScanTemplateId(templates: TemplateSummary[]): number | '' {
  if (templates.length === 0) return '';
  return templates.find((t) => t.is_default)?.id ?? templates[0].id;
}

function pickDefaultScoreCardTemplateId(email?: string | null): ScoreCardHtmlTemplateId {
  const normalizedEmail = (email ?? '').toLowerCase();

  if (normalizedEmail.includes('mso') || normalizedEmail.includes('ambition')) {
    return 'ambition-default';
  }

  if (normalizedEmail.includes('symplr')) {
    return 'symplr';
  }

  return 'compact';
}

export default function ScanPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, error } = useSelector((state: RootState) => state.scan);
  const [url, setUrl] = useState('');
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | ''>('');
  const [selectedScoreCardTemplate, setSelectedScoreCardTemplate] = useState<ScoreCardHtmlTemplateId>(
    pickDefaultScoreCardTemplateId(user?.email),
  );
  const [startProgress, setStartProgress] = useState(0);

  useEffect(() => {
    if (!loading) {
      setStartProgress(0);
      return;
    }

    setStartProgress(10);
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const next = 10 + (1 - Math.exp(-elapsedMs / 7000)) * 80;
      setStartProgress(Math.min(92, next));
    }, 250);

    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    api.get<{ templates: TemplateSummary[] }>('/scans/templates').then((res) => {
      if (res.success) {
        const nextTemplates = res.data.templates;
        setTemplates(nextTemplates);
        setSelectedTemplate(pickDefaultScanTemplateId(nextTemplates));
      }
    });
  }, []);

  useEffect(() => {
    setSelectedScoreCardTemplate(pickDefaultScoreCardTemplateId(user?.email));
  }, [user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearScanError());

    const target = url.startsWith('http') ? url : `https://${url}`;
    const result = await dispatch(startScan({
      url: target,
      ...(selectedTemplate ? { scan_template_id: selectedTemplate } : {}),
    }));

    if (startScan.fulfilled.match(result)) {
      navigate(`/scan/${result.payload.id}?template=${encodeURIComponent(selectedScoreCardTemplate)}`);
    }
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 6, mt: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
          Domain{' '}
          <Box component="span" sx={{ color: colors.lime, bgcolor: colors.black, px: 1.5, py: 0.5, borderRadius: 1 }}>
            Screener
          </Box>
          {import.meta.env.VITE_APP_VERSION && (
            <Box
              component="span"
              sx={{
                ml: 1.5,
                fontSize: '1em',
                fontWeight: 600,
                color: '#000',
                verticalAlign: 'baseline',
              }}
            >
              v{import.meta.env.VITE_APP_VERSION}
            </Box>
          )}
        </Typography>
        {import.meta.env.VITE_RELEASE_DATE && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.5, opacity: 0.7 }}>
            ({import.meta.env.VITE_RELEASE_DATE})
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Analysiere jede URL auf Tracking-Setup-Qualität, DSGVO-Consent-Verhalten
          und CMP-Implementierung.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          maxWidth: 640,
          mx: 'auto',
          p: 4,
          bgcolor: colors.lightGrey,
          border: `1px solid ${colors.silver}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearScanError())}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(245,245,245,0.92)', backdropFilter: 'blur(2px)',
          }}>
            <Box sx={{ width: '82%', maxWidth: 420, mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={startProgress}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    bgcolor: colors.lime,
                  },
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Scan wird durchgeführt...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Die URL wird gerade analysiert. Das kann einige Minuten dauern.
            </Typography>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            placeholder="z.B. https://www.example.de"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            autoFocus
            disabled={loading}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.white,
              },
            }}
          />

          {/* Template selector: hidden for USER role (always uses default) */}
          {user?.role !== UserRole.USER && templates.length > 1 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Scan Template</InputLabel>
              <Select
                value={selectedTemplate}
                label="Scan Template"
                onChange={(e) => setSelectedTemplate(e.target.value as number | '')}
                disabled={loading}
                sx={{ bgcolor: colors.white }}
                renderValue={(val) => {
                  const tpl = templates.find((t) => t.id === val);
                  if (!tpl) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tpl.emoji && (
                        tpl.emoji.startsWith('/uploads/')
                          ? <Box component="img" src={tpl.emoji} alt="" sx={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
                          : <Box component="span" sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{tpl.emoji}</Box>
                      )}
                      {tpl.display_name}{tpl.is_default ? ' (Default)' : ''}
                    </Box>
                  );
                }}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t.emoji && (
                      t.emoji.startsWith('/uploads/')
                        ? <Box component="img" src={t.emoji} alt="" sx={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }} />
                        : <Box component="span" sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{t.emoji}</Box>
                    )}
                    {t.display_name}{t.is_default ? ' (Default)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Scorecard</InputLabel>
            <Select
              value={selectedScoreCardTemplate}
              label="Scorecard"
              onChange={(e) => setSelectedScoreCardTemplate(e.target.value as ScoreCardHtmlTemplateId)}
              disabled={loading}
              sx={{ bgcolor: colors.white }}
            >
              {SCORE_CARD_HTML_TEMPLATE_IDS.map((templateId) => (
                <MenuItem key={templateId} value={templateId}>
                  {SCORE_CARD_HTML_TEMPLATE_LABELS[templateId]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || !url.trim()}
            size="large"
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {loading ? 'Scan wird gestartet...' : 'URL scannen'}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Scans werden über Google Cloud Tasks verarbeitet
        </Typography>
      </Box>
    </Box>
  );
}
